import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null); // { title, detail, status }
  const [busy, setBusy] = useState(true);

  const runExchange = useCallback(async () => {
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const session_id = params.get("session_id");
    if (!session_id) {
      navigate("/login", { replace: true });
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/session", { session_id });
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      const perms = Array.isArray(me?.permissions) ? me.permissions : [];
      const target = me?.is_master || perms.includes("dashboard.view")
        ? "/dashboard"
        : perms.includes("expedition.request")
          ? "/expedition"
          : perms.includes("expedition.admin.view")
            ? "/expedition/admin"
            : "/dashboard";
      window.history.replaceState(null, "", target);
      navigate(target, { replace: true, state: { user: me } });
    } catch (e) {
      const status = e?.response?.status;
      const serverDetail = e?.response?.data?.detail;
      // Classify so the user sees a message that matches reality
      let title = "Falha ao autenticar";
      let detail = serverDetail || "Não conseguimos concluir seu login agora.";
      if (!e?.response || e?.code === "ERR_NETWORK") {
        title = "Servidor temporariamente indisponível";
        detail = "O servidor não respondeu (possível manutenção/deploy em andamento). Aguarde alguns segundos e tente de novo.";
      } else if (status === 502 || status === 503 || status === 504 || status === 520) {
        title = "Servidor temporariamente indisponível";
        detail = `Erro ${status} do servidor. Isso costuma ser transiente — tente de novo em alguns segundos.`;
      } else if (status === 401) {
        title = "Sessão expirada";
        detail = "Seu link de login expirou. Clique em “Voltar e entrar de novo”.";
      }
      // eslint-disable-next-line no-console
      console.error("AuthCallback error:", e);
      setError({ title, detail, status });
    } finally {
      setBusy(false);
    }
  }, [navigate, setUser]);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    runExchange();
  }, [runExchange]);

  const retry = () => {
    hasProcessed.current = true; // still true, we are retrying manually
    runExchange();
  };

  const backToLogin = () => {
    window.history.replaceState(null, "", "/login");
    navigate("/login", { replace: true });
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-bg p-4">
        <div data-testid="auth-error-card" className="card-brutal bg-brand-cream max-w-md w-full">
          <div className="p-5 border-b-2 border-brand-ink bg-brand-peach flex items-center gap-3">
            <Warning weight="bold" size={28} />
            <div>
              <p className="label-brutal">Autenticação</p>
              <h2 className="font-heading text-2xl font-black leading-none">{error.title}</h2>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-brand-ink">{error.detail}</p>
            {error.status ? (
              <p className="text-xs text-brand-ink2">Código: <strong>{error.status}</strong></p>
            ) : null}
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                data-testid="auth-retry-btn"
                onClick={retry}
                disabled={busy}
                className="btn-pink h-11 disabled:opacity-40"
              >
                <ArrowClockwise weight="bold" size={16} />
                {busy ? "Tentando…" : "Tentar de novo"}
              </button>
              <button
                data-testid="auth-back-login-btn"
                onClick={backToLogin}
                className="btn-white h-11"
              >
                Voltar e entrar de novo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg">
      <div className="card-brutal p-8 animate-fade-in">
        <p className="font-heading text-2xl font-black">Autenticando…</p>
        <p className="text-brand-ink2 mt-2">Preparando seu ateliê digital.</p>
      </div>
    </div>
  );
}
